import { Meta } from '@/src/components/common/Meta';
import { JAM_EVENT_DETAILS_ROUTES } from '@/src/routes';
import { useFlashbars } from '@/src/store/flashbar.context';
import { useJamChallenge } from '@/src/store/jam-challenge.context';
import { useJamEventDetails } from '@/src/store/jam-event-details.context';
import { useJamFeedback } from '@/src/store/jam-feedback';
import { JamEventFeedback } from '@/src/types/JamFeedback';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { Box, Button, SpaceBetween } from '@amzn/awsui-components-react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import LearnSomethingAndFeedback from './LearnSomethingAndFeedback';
import RatingContainer from './RatingContainer';

interface IEventFeedbackData {
  challengeFeedback?: any;
  eventFeedback?: any;
}

const EventFeedback: React.FC<{ onFeedbackDone?: () => void }> = ({ onFeedbackDone }) => {
  const { t } = useTranslation();
  const { saveJamEventFeedbackData } = useJamFeedback();
  const { eventFeedbackData } = useJamChallenge();
  const { clearFlashbars } = useFlashbars();
  const { eventName, event } = useJamEventDetails();
  const history = useHistory();

  const [eventRatingRequired, setEventRatingRequired] = React.useState('');
  const [speakerRatingRequired, setSpeakerRatingRequired] = React.useState('');
  const [learnSomethingRequired, setLearnSomethingRequired] = React.useState('');
  const [eventFeedback, setEventFeedback] = React.useState<JamEventFeedback>({
    eventRank: 0,
    speakerRank: 0,
    notes: '',
    didYouLearnSomethingNew: '',
  });

  useEffect(() => {
    if (eventFeedbackData && (eventFeedbackData as unknown as IEventFeedbackData)?.eventFeedback) {
      const feedback: JamEventFeedback = eventFeedbackData.eventFeedback;
      if (feedback) {
        const obj = {
          eventRank: feedback.eventRank || 0,
          speakerRank: feedback.speakerRank || 0,
          notes: feedback.notes || '',
          didYouLearnSomethingNew:
            feedback.didYouLearnSomethingNew === true ? 'yes' : feedback.didYouLearnSomethingNew === false ? 'no' : '',
        };
        setEventFeedback(obj);
      }
    }
  }, [eventFeedbackData]);

  useEffect(() => {
    const errorText = t(i18nKeys.myJams.feedback.label.rating.ratingRequired);

    setEventRatingRequired(!eventFeedback.eventRank ? errorText : '');
    setSpeakerRatingRequired(!eventFeedback.speakerRank ? errorText : '');
    setLearnSomethingRequired(eventFeedback.didYouLearnSomethingNew === '' ? errorText : '');
  }, [eventFeedback.eventRank, eventFeedback.speakerRank, eventFeedback.didYouLearnSomethingNew]);

  const handleEventRadioAndComment = (name: string, value: string) => {
    setEventFeedback({ ...eventFeedback, [name]: value });
  };

  const handleSubmit = () => {
    clearFlashbars();
    void saveJamEventFeedbackData({
      body: {
        ...eventFeedback,
        didYouLearnSomethingNew: eventFeedback.didYouLearnSomethingNew === 'yes' ? true : false,
      },
    }).finally(() => {
      onFeedbackDone?.();
    });
    history.push(JAM_EVENT_DETAILS_ROUTES.Challenges.resolve([eventName]));
  };

  const eventTitle = event?.title ?? t(i18nKeys.meta.event.title);
  const pageTitle = `${t(i18nKeys.meta.feedback.title)} | ${eventTitle}`;

  return (
    <>
      <Meta title={pageTitle} />
      <SpaceBetween direction="vertical" size="m">
        <RatingContainer
          label={t(i18nKeys.myJams.feedback.label.rating.overAll)}
          constraintText={eventRatingRequired}
          rating={eventFeedback.eventRank}
          setRating={(value) => {
            setEventFeedback({ ...eventFeedback, eventRank: value });
          }}
          data-testid="overall-rating"
        />
        <RatingContainer
          label={t(i18nKeys.myJams.feedback.label.rating.staff)}
          constraintText={speakerRatingRequired}
          rating={eventFeedback.speakerRank}
          setRating={(value) => {
            setEventFeedback({ ...eventFeedback, speakerRank: value });
          }}
          data-testid="facilitator-rating"
        />
        <LearnSomethingAndFeedback
          radio={eventFeedback.didYouLearnSomethingNew}
          radioConstraintText={learnSomethingRequired}
          textArea={eventFeedback.notes}
          radioName="didYouLearnSomethingNew"
          textAreaName="notes"
          setValue={handleEventRadioAndComment}
          textAreaConstraintText=""
        />
        <Box float="right">
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={Boolean(eventRatingRequired || speakerRatingRequired || learnSomethingRequired)}
            data-testid="submit-event-feedback-btn"
          >
            {t(i18nKeys.myJams.feedback.buttons.submitFeedback)}
          </Button>
        </Box>
      </SpaceBetween>
    </>
  );
};

export default EventFeedback;
